import type { PropsWithChildren, ReactNode } from 'react';
import React, { useEffect, useState } from 'react';

import { TabContext, TabPanel } from '@mui/lab';
import { Box, styled } from '@mui/material';

import PlannerItemNavigationPanel from '@compositeComponents/organizer/PlannerItemNavigationPanel';

interface TabContent<T extends string> {
  id: T;
  content: ReactNode;
}

interface UniversalRightColumnProps<T extends string> {
  isTablet: boolean;
  navigationMenu: any;
  tabContents: TabContent<T>[];
  defaultTabletTabId: T;
  defaultDesktopTabId: T;
}

const ModalTabsContentContainer = styled(Box)(() => ({
  display: 'flex',
  width: '100%',
  height: '100%',
  overflow: 'auto',
}));

const ModalTabPanelContainer = styled(TabPanel)(() => ({
  padding: '16px',
  height: '100%',
}));
const ModalTabsFirstBlockContainer = styled(Box, {
  shouldForwardProp: (prop: string) => !['isDetailsTab'].includes(prop),
})<{ isDetailsTab: boolean }>(({ theme, isDetailsTab }) => ({
  backgroundColor: isDetailsTab ? theme.palette.case.neutral.n0 : theme.palette.case.neutral.n75,
  border: `1px solid ${theme.palette.case.neutral.n200}`,
  borderTop: 'none',
  borderBottom: 'none',
  width: '100%',
  maxWidth: '360px',
  minWidth: '360px',
  height: '100%',

  '@media (max-width: 1100px)': {
    borderLeft: 'none',
    borderBottomLeftRadius: '8px',
  },
}));

export const RightColumnTabModal = <T extends string>(
  props: PropsWithChildren<UniversalRightColumnProps<T>>,
) => {
  const { isTablet, navigationMenu, tabContents, defaultTabletTabId, defaultDesktopTabId, children } = props;

  const [value, setValue] = useState<T>(isTablet ? defaultTabletTabId : defaultDesktopTabId);
  const handleChange = (newValue: T) => {
    if (newValue !== value) {
      setValue(newValue);
    }
  };

  useEffect(() => {
    if (isTablet) handleChange(defaultTabletTabId);
    else handleChange(defaultDesktopTabId);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTablet]);

  return (
    <Box sx={{ width: isTablet ? '100%' : 'initial' }}>
      <TabContext value={value}>
        <ModalTabsContentContainer>
          <ModalTabsFirstBlockContainer
            isDetailsTab={value === defaultTabletTabId}
            sx={{
              maxWidth: isTablet ? 'calc(100% - 74px)' : '360px',
              minWidth: isTablet ? 'calc(100% - 74px)' : '360px',
            }}
          >
            {children && (
              <TabPanel sx={{ height: '100%', p: '0' }} value={defaultTabletTabId}>
                {children}
              </TabPanel>
            )}

            {tabContents.map((tab) => (
              <ModalTabPanelContainer key={tab.id} value={tab.id}>
                {tab.content}
              </ModalTabPanelContainer>
            ))}
          </ModalTabsFirstBlockContainer>

          <PlannerItemNavigationPanel tabs={navigationMenu} handleChange={handleChange} />
        </ModalTabsContentContainer>
      </TabContext>
    </Box>
  );
};
